// src/components/challenges/MyWellnessChallengesCard.tsx
import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type {
  WellnessUserChallenge,
  WellnessChallengeCheckin
} from '@/types/wellnessChallenges';

interface ChallengeWithTodayCheckin extends WellnessUserChallenge {
  todayCheckin?: WellnessChallengeCheckin | null;
}

export default function MyWellnessChallengesCard() {
  const [challenges, setChallenges] = useState<ChallengeWithTodayCheckin[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    const fetchChallenges = async () => {
      setLoading(true);

      const { data: userChallenges, error } = await supabase
        .from('wellness_user_challenges')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching wellness challenges', error);
        setLoading(false);
        return;
      }

      const challengeIds = (userChallenges || []).map((c: any) => c.id);
      if (challengeIds.length === 0) {
        setChallenges([]);
        setLoading(false);
        return;
      }

      const { data: checkins, error: checkinsError } = await supabase
        .from('wellness_challenge_checkins')
        .select('*')
        .in('user_challenge_id', challengeIds)
        .eq('checkin_date', today);

      if (checkinsError) {
        console.error('Error fetching challenge checkins', checkinsError);
      }

      const checkinsByChallenge = new Map<string, WellnessChallengeCheckin>();
      (checkins || []).forEach((c: any) => {
        checkinsByChallenge.set(c.user_challenge_id, c as WellnessChallengeCheckin);
      });

      const merged: ChallengeWithTodayCheckin[] = (userChallenges || []).map(
        (c: any) => ({
          ...(c as WellnessUserChallenge),
          todayCheckin: checkinsByChallenge.get(c.id) || null
        })
      );

      setChallenges(merged);
      setLoading(false);
    };

    fetchChallenges();
  }, [today]);

  const handleCheckin = async (challengeId: string, completed: boolean) => {
    setSavingId(challengeId);

    const payload = {
      user_challenge_id: challengeId,
      checkin_date: today,
      completed
    };

    const { data, error } = await supabase
      .from('wellness_challenge_checkins')
      .upsert(payload, { onConflict: 'user_challenge_id,checkin_date' })
      .select()
      .single();

    if (error) {
      console.error('Error saving wellness challenge checkin', error);
      alert('Unable to save your check-in right now.');
      setSavingId(null);
      return;
    }

    setChallenges(prev =>
      prev.map(ch =>
        ch.id === challengeId
          ? { ...ch, todayCheckin: data as WellnessChallengeCheckin }
          : ch
      )
    );

    setSavingId(null);
  };

  if (loading) {
    return null;
  }

  if (!challenges.length) {
    return (
      <div className="border rounded-xl p-4 bg-background shadow-sm">
        <h3 className="text-base font-semibold text-foreground">My challenges</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          You don't have any active personal challenges yet. When you start one, it will appear here so you can check in quickly and let Therappi track the impact over time.
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-xl p-4 bg-background shadow-sm">
      <h3 className="text-base font-semibold text-foreground">My challenges</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        Small, repeated choices create the biggest shifts. Check in honestly - there's no "good" or "bad" here, only information.
      </p>

      <div className="mt-3 space-y-3">
        {challenges.map(ch => {
          const title = ch.custom_title || 'Personal focus';
          const hasCheckin = !!ch.todayCheckin;
          const positive = ch.todayCheckin?.completed === true;

          return (
            <div
              key={ch.id}
              className="border rounded-lg px-3 py-2 bg-muted/50 flex flex-col gap-2"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-foreground">{title}</p>
                  {ch.custom_goal && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {ch.custom_goal}
                    </p>
                  )}
                </div>
                <span className="text-[11px] text-muted-foreground">
                  {ch.start_date} → {ch.end_date}
                </span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleCheckin(ch.id, true)}
                  disabled={savingId === ch.id}
                  className={`flex-1 px-3 py-1.5 rounded-full text-xs transition-colors ${
                    positive
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-primary/10 text-primary hover:bg-primary/20'
                  }`}
                >
                  {positive ? 'Logged as done today' : 'Yes, I did this today'}
                </button>
                <button
                  onClick={() => handleCheckin(ch.id, false)}
                  disabled={savingId === ch.id}
                  className={`flex-1 px-3 py-1.5 rounded-full text-xs transition-colors ${
                    hasCheckin && !positive
                      ? 'bg-muted text-foreground'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {hasCheckin && !positive ? 'Logged as not today' : 'Not today'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
